import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, formatApiErrorDetail } from "../contexts/AuthContext";
import { Eye, EyeOff, Loader2 } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const data = await login(email, password);
      if (data.role === "admin") navigate("/admin");
      else if (!data.onboarded) navigate("/onboarding");
      else navigate("/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6 py-16">
      <div className="w-full max-w-md">
        <div className="text-center mb-10">
          <Link to="/" className="font-outfit text-3xl font-bold text-[#D4AF37]">Prestigious Wireless</Link>
          <p className="text-[#A0A0A0] mt-3 font-manrope">Sign in to your account</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-[#0F0F0F] border border-[#2A2A2A] rounded-2xl p-8 space-y-6" data-testid="login-form">
          {error && (
            <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-xl text-sm font-manrope" data-testid="login-error">
              {error}
            </div>
          )}

          <div>
            <label className="block text-[#A0A0A0] text-sm font-manrope mb-2">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-[#0F0F0F] border border-[#2A2A2A] focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] rounded-xl px-4 py-3 text-[#F5F5F5] font-manrope outline-none transition-all"
              placeholder="you@example.com"
              required
              data-testid="login-email-input"
            />
          </div>

          <div>
            <label className="block text-[#A0A0A0] text-sm font-manrope mb-2">Password</label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-[#0F0F0F] border border-[#2A2A2A] focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] rounded-xl px-4 py-3 text-[#F5F5F5] font-manrope outline-none transition-all pr-12"
                placeholder="Your password"
                required
                data-testid="login-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-[#A0A0A0] hover:text-[#F5F5F5]"
                data-testid="login-toggle-password"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            <div className="flex justify-end mt-1">
              <Link to="/forgot-password" className="text-[#D4AF37] hover:text-[#F0C541] text-xs font-manrope" data-testid="forgot-password-link">Forgot password?</Link>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-[#D4AF37] hover:bg-[#F0C541] text-black py-3.5 rounded-full font-semibold text-lg transition-all duration-300 hover:-translate-y-0.5 disabled:opacity-50 disabled:hover:translate-y-0 flex items-center justify-center gap-2"
            data-testid="login-submit-button"
          >
            {loading ? <><Loader2 className="w-5 h-5 animate-spin" /> Signing in...</> : "Sign In"}
          </button>

          <p className="text-center text-[#A0A0A0] text-sm font-manrope">
            Don't have an account?{" "}
            <Link to="/signup" className="text-[#D4AF37] hover:text-[#F0C541] font-semibold" data-testid="login-register-link">
              Create one
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
